import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/useAuthStore';
import { Platform } from '../utils/platform';

interface UseDeveloperModeResult {
  isDeveloper: boolean;
  isLoading: boolean;
  error: string | null;
  checkDeveloperStatus: (userId: string) => Promise<boolean>;
}

/**
 * Hook for checking if the current user has developer access
 * 
 * This checks the user's profile for the is_developer flag
 * Falls back to a local override if the column doesn't exist
 */
export function useDeveloperMode(): UseDeveloperModeResult {
  const { user } = useAuthStore();
  const [isDeveloper, setIsDeveloper] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Added flag to track if the developer column exists
  const [columnExists, setColumnExists] = useState<boolean>(true);
  
  const checkDeveloperStatus = async (userId: string): Promise<boolean> => {
    if (!userId) return false;
    
    try {
      setIsLoading(true);
      setError(null);

      // First check if the profiles table has the is_developer column
      const { data: developerFlag, error: developerError } = await supabase
        .from('profiles')
        .select('is_developer')
        .eq('id', userId)
        .single();

      // Handle column doesn't exist error (PostgreSQL error code)
      if (developerError && developerError.code === '42703') {
        console.log('is_developer column does not exist in profiles table');
        setColumnExists(false);
        
        // Check for developer override in local storage
        let isDevOverride = false;
        
        if (Platform.isWeb) {
          isDevOverride = localStorage.getItem('flows_dev_mode') === 'true';
        } else if (typeof global !== 'undefined') {
          if ((global as any).__FLOWS_DEV_MODE__) {
            isDevOverride = (global as any).__FLOWS_DEV_MODE__ === true;
          }
        }
        
        console.log(`Using developer override: ${isDevOverride}`);
        setIsDeveloper(isDevOverride);
        return isDevOverride;
      }
      
      // Handle other errors
      if (developerError) {
        console.error('Error checking developer status:', developerError);
        throw developerError;
      }

      const isDev = developerFlag?.is_developer === true;
      setIsDeveloper(isDev);
      return isDev;
    } catch (error) {
      console.error('Error in checkDeveloperStatus:', error);
      setError(error instanceof Error ? error.message : 'Failed to check developer status');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      checkDeveloperStatus(user.id).catch(err => {
        console.error('Failed to check developer status:', err);
      });
    } else {
      setIsDeveloper(false);
      setIsLoading(false);
    }
  }, [user]);

  return {
    isDeveloper,
    isLoading,
    error,
    checkDeveloperStatus
  };
} 